import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  getApiUrl,
  getCsrfToken,
  getJobsUrl,
  assertTestAllowed,
  readJobsApiTarget,
  registerProdRunnerJob,
  startLocalHybridJob,
} from "../../utils/apiHelper";
import { X } from "lucide-react";
import { profileTypeLabels } from "../../utils/categoryLabels";
import "../../styles/theme.css";

function RunTestModal({ isOpen, onClose, onTestStarted, onScheduleTest }) {
  const { t } = useTranslation("runTestsModal");
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [profilesError, setProfilesError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [runningTest, setRunningTest] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [reportName, setReportName] = useState("");

  const reloadProfiles = useCallback(async () => {
    try {
      // Removed: user_id query parameter - user is obtained from session

      const res = await fetch(getApiUrl('/api/internal/profiles-config/getAllProfiles/'), {
        method: "GET",
        credentials: 'include', // Send session cookies
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setProfiles(list);
      setProfilesError(false);
      if (Array.isArray(data) && data.length > 0) {
        setSelectedProfileId(String(data[0].id));
      }
    } catch (err) {
      console.error("Failed to load profiles", err);
      setProfiles([]);
      setProfilesError(true);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      reloadProfiles();
      setSearchQuery("");
      setReportName("");
    }
  }, [isOpen, reloadProfiles]);

  // Filter profiles based on search query
  const filteredProfiles = profiles.filter((profile) => {
    const searchLower = searchQuery.toLowerCase();
    const nameMatch = profile.profile_name?.toLowerCase().includes(searchLower);
    const typeMatch = (profileTypeLabels[profile.profile_type] || profile.profile_type || '').toLowerCase().includes(searchLower);
    return nameMatch || typeMatch;
  });

  const handleScheduleTest = () => {
    if (!selectedProfileId || profiles.length === 0) {
      alert(t("selectProfile"));
      return;
    }
    // Close this modal and open schedule modal
    onClose();
    if (onScheduleTest) {
      onScheduleTest(selectedProfileId);
    }
  };

  const handleRunTest = async () => {
    if (!selectedProfileId || profiles.length === 0) {
      alert(t("selectProfile"));
      return;
    }

    try {
      setRunningTest(true);

      await assertTestAllowed();

      const formData = new FormData();
      formData.append('profile_id', selectedProfileId);
      if (reportName.trim()) {
        formData.append('report_name', reportName.trim());
      }

      const target = readJobsApiTarget();
      let response;
      if (target === "localhost") {
        const registered = await registerProdRunnerJob(formData);
        response = await startLocalHybridJob({
          profileId: selectedProfileId,
          reportName: reportName.trim(),
          registered,
        });
      } else {
        const csrfToken = await getCsrfToken();
        response = await fetch(getJobsUrl("", { force: "server" }), {
          method: "POST",
          credentials: "include",
          headers: {
            "X-CSRFToken": csrfToken,
          },
          body: formData,
        });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Test started successfully:', result);
      
      // Check if there's an error in the response
      if (result.error) {
        throw new Error(result.message || 'Failed to start test');
      }
      
      // Check if we got a valid response with job_id
      if (result.job_id && (result.status === 'RUNNING' || result.status === 'PENDING')) {
        // Close modal first
        onClose();
        
        // Call callback with job_id (caller can navigate/open status modal)
        if (onTestStarted) {
          onTestStarted(result.job_id);
        }
      } else {
        throw new Error('Unexpected response format from server');
      }
      
    } catch (error) {
      console.error('Error starting test:', error);
      const raw = error.message || "";
      const message =
        raw === "Failed to fetch" || raw.toLowerCase().includes("network")
          ? "Could not reach the local runner at http://127.0.0.1:8000. Start it with WH_MODE=hybrid, then try again."
          : raw || "Unable to start test. Please try again.";
      alert(message);
    } finally {
      setRunningTest(false);
    }
  };

  if (!isOpen) return null;

  const jobsApiTarget = readJobsApiTarget();

  // Determine if dark theme is active
  const isDarkTheme = document.documentElement.getAttribute('data-theme') === 'dark';
  const borderColor = isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.15)';
  const itemBorderColor = isDarkTheme ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)';

  return (
    <div 
        className="modal-backdrop" 
        style={{
          zIndex: 9999
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
      <div 
        className="modal-content"
        style={{
          animation: "fadeIn 0.3s ease-out"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            background: "transparent",
            border: "none",
            fontSize: "24px",
            cursor: "pointer",
            color: "var(--card-text)",
            zIndex: 10000
          }}
        >
          <X size={24} />
        </button>

        <h3>{t("title")}</h3>

        <input
          type="text"
          placeholder={t("reportNamePlaceholder")}
          value={reportName}
          onChange={(e) => setReportName(e.target.value)}
          style={{
            width: "100%",
            padding: "12px 16px",
            marginBottom: "16px",
            borderRadius: "8px",
            border: `2px solid ${borderColor}`,
            backgroundColor: "var(--card-bg)",
            color: "var(--card-text)",
            fontSize: "14px",
            outline: "none",
            boxSizing: "border-box",
            transition: "border-color 0.2s ease, box-shadow 0.2s ease"
          }}
          onFocus={(e) => {
            e.target.style.borderColor = "var(--btn-gradient-start)";
            e.target.style.boxShadow = "0 0 0 3px rgba(82, 106, 231, 0.1)";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = borderColor;
            e.target.style.boxShadow = "none";
          }}
        />

        {profilesError ? (
          <div style={{ color: "#d32f2f", textAlign: "center", padding: "20px 0" }}>
            <p style={{ marginBottom: "15px" }}>{t("errorLoadingProfiles")}</p>
            <button className="btn btn-secondary" onClick={reloadProfiles}>
              {t("retry")}
            </button>
          </div>
        ) : (
          <>
            <input
              type="text"
              placeholder={t("searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 16px",
                marginBottom: "16px",
                borderRadius: "8px",
                border: `2px solid ${borderColor}`,
                backgroundColor: "var(--card-bg)",
                color: "var(--card-text)",
                fontSize: "14px",
                outline: "none",
                boxSizing: "border-box",
                transition: "border-color 0.2s ease, box-shadow 0.2s ease"
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "var(--btn-gradient-start)";
                e.target.style.boxShadow = "0 0 0 3px rgba(82, 106, 231, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = borderColor;
                e.target.style.boxShadow = "none";
              }}
            />

            <div
              style={{
                maxHeight: "250px",
                overflowY: "auto",
                overflowX: "hidden",
                marginBottom: "20px",
                borderRadius: "8px",
                border: `2px solid ${borderColor}`,
                backgroundColor: "var(--card-bg)",
                boxSizing: "border-box"
              }}
            >
              {profiles.length === 0 ? (
                <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--card-text)", opacity: 0.6 }}>
                  {t("loading")}
                </div>
              ) : filteredProfiles.length === 0 ? (
                <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--card-text)", opacity: 0.6 }}>
                  {t("noResults")}
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${borderColor}` }}>
                      <th style={{ padding: "10px 12px", textAlign: "left", color: "var(--card-text)", fontSize: "12px", fontWeight: "600", width: "40px" }}></th>
                      <th style={{ padding: "10px 12px", textAlign: "left", color: "var(--card-text)", fontSize: "12px", fontWeight: "600", width: "55%" }}>{t("profileName")}</th>
                      <th style={{ padding: "10px 12px", textAlign: "left", color: "var(--card-text)", fontSize: "12px", fontWeight: "600", width: "45%" }}>{t("profileType")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProfiles.map((profile) => {
                      const isSelected = selectedProfileId === String(profile.id);
                      return (
                        <tr
                          key={profile.id}
                          onClick={() => setSelectedProfileId(String(profile.id))}
                          style={{
                            cursor: "pointer",
                            borderBottom: `1px solid ${itemBorderColor}`,
                            backgroundColor: isSelected 
                              ? "rgba(82, 106, 231, 0.1)" 
                              : "transparent",
                            transition: "background-color 0.15s"
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = "transparent";
                            }
                          }}
                        >
                          <td style={{ padding: "12px", textAlign: "center", verticalAlign: "top" }}>
                            <input
                              type="radio"
                              checked={isSelected}
                              onChange={() => setSelectedProfileId(String(profile.id))}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                cursor: "pointer",
                                accentColor: "var(--btn-gradient-start)"
                              }}
                            />
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              color: "var(--card-text)",
                              fontSize: "14px",
                              fontWeight: "500",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                              whiteSpace: "normal",
                              verticalAlign: "top",
                            }}
                          >
                            {profile.profile_name || t("unnamedProfile")}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              color: "var(--card-text)",
                              fontSize: "14px",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                              whiteSpace: "normal",
                              verticalAlign: "top",
                            }}
                          >
                            {profileTypeLabels[profile.profile_type] || profile.profile_type || t("notAvailable")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button 
                className="btn btn-secondary" 
                onClick={onClose}
                disabled={runningTest}
              >
                {t("cancel")}
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={handleScheduleTest}
                disabled={runningTest || !selectedProfileId || profiles.length === 0 || jobsApiTarget === "localhost"}
                title={jobsApiTarget === "localhost" ? "Schedule runs on the server only" : undefined}
              >
                {t("scheduleTest")}
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleRunTest}
                disabled={runningTest || !selectedProfileId || profiles.length === 0}
              >
                {runningTest ? t("starting") : t("runTest")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default RunTestModal;
